import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { SiteSummary, User } from '../models';
import { AuthService } from './auth.service';

interface ApiSiteResponse {
  id: string;
  name: string;
  slug?: string;
  description?: string | null;
}

interface SiteListResponse {
  data: ApiSiteResponse[];
}

@Injectable({
  providedIn: 'root',
})
export class SiteService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly ACTIVE_SITE_KEY = 'pos_active_site_id';

  // Full, manageable site list (from GET /sites) — used by admin/management UIs
  // and assignment forms. NOT used to drive the site switcher.
  private readonly sitesSubject = new BehaviorSubject<SiteSummary[]>([]);

  // Sites the authenticated user is actually assigned to (site_user pivot).
  // This drives the navbar switcher and the active-site context.
  private readonly switchableSitesSubject = new BehaviorSubject<SiteSummary[]>(
    [],
  );

  private readonly activeSiteIdSubject = new BehaviorSubject<string | null>(
    null,
  );
  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);

  readonly sites$ = this.sitesSubject.asObservable();
  readonly switchableSites$ = this.switchableSitesSubject.asObservable();
  readonly isLoading$ = this.isLoadingSubject.asObservable();

  readonly activeSite$ = combineLatest([
    this.switchableSites$,
    this.activeSiteIdSubject.asObservable(),
  ]).pipe(
    map(
      ([sites, activeId]) => sites.find((site) => site.id === activeId) ?? null,
    ),
    distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
  );

  // A user can switch sites only when assigned to more than one.
  readonly canSwitchSites$ = this.switchableSites$.pipe(
    map((sites) => sites.length > 1),
    distinctUntilChanged(),
  );

  private readonly userSubscription: Subscription;

  constructor() {
    const storedSiteId = localStorage.getItem(this.ACTIVE_SITE_KEY);
    if (storedSiteId) {
      this.activeSiteIdSubject.next(storedSiteId);
    }

    this.userSubscription = this.authService.currentUser$
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id))
      .subscribe((user) => {
        if (!user) {
          this.resetState();
          return;
        }

        // Restrict the switcher to the user's assigned sites and select the
        // appropriate active site (their primary on first login).
        const assigned = this.resolveAssignedSites(user);
        this.switchableSitesSubject.next(assigned);
        this.initializeActiveSite(assigned);

        // Load the full site list in the background for management/forms.
        this.refreshSites();
      });
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
    this.sitesSubject.complete();
    this.switchableSitesSubject.complete();
    this.activeSiteIdSubject.complete();
    this.isLoadingSubject.complete();
  }

  refreshSites(): void {
    this.isLoadingSubject.next(true);

    this.http
      .get<SiteListResponse>(`${environment.apiUrl}/sites`)
      .pipe(
        map((response) => response.data.map((site) => this.mapSite(site))),
        finalize(() => this.isLoadingSubject.next(false)),
      )
      .subscribe({
        next: (sites) => {
          this.sitesSubject.next(sites);
        },
        error: (error) => {
          console.error('Failed to load sites', error);
        },
      });
  }

  setActiveSite(siteId: string): void {
    if (!siteId) {
      this.activeSiteIdSubject.next(null);
      localStorage.removeItem(this.ACTIVE_SITE_KEY);
      return;
    }

    // Only allow switching to a site the user is actually assigned to.
    const exists = this.switchableSitesSubject.value.some(
      (site) => site.id === siteId,
    );
    if (!exists) {
      return;
    }

    this.activeSiteIdSubject.next(siteId);
    localStorage.setItem(this.ACTIVE_SITE_KEY, siteId);
  }

  getActiveSiteId(): string | null {
    return this.activeSiteIdSubject.value;
  }

  getActiveSiteValue(): SiteSummary | null {
    const activeId = this.activeSiteIdSubject.value;
    return (
      this.switchableSitesSubject.value.find((site) => site.id === activeId) ??
      null
    );
  }

  /**
   * Determine which sites the authenticated user may operate in. Falls back to
   * the single primary site when the assigned list is unavailable.
   */
  private resolveAssignedSites(user: User): SiteSummary[] {
    if (user.sites?.length) {
      return user.sites;
    }

    return user.site ? [user.site] : [];
  }

  /**
   * Select the active site after login or a user change. Keeps a previously
   * selected site if it is still valid; otherwise defaults to the user's
   * primary site so their site loads automatically on login.
   */
  private initializeActiveSite(assignedSites: SiteSummary[]): void {
    const storedId =
      this.activeSiteIdSubject.value ??
      localStorage.getItem(this.ACTIVE_SITE_KEY);

    let nextId =
      storedId && assignedSites.some((site) => site.id === storedId)
        ? storedId
        : null;

    if (!nextId) {
      nextId =
        assignedSites.find((site) => site.isPrimary)?.id ??
        assignedSites[0]?.id ??
        null;
    }

    this.activeSiteIdSubject.next(nextId);

    if (nextId) {
      localStorage.setItem(this.ACTIVE_SITE_KEY, nextId);
    } else {
      localStorage.removeItem(this.ACTIVE_SITE_KEY);
    }
  }

  private mapSite(site: ApiSiteResponse): SiteSummary {
    return {
      id: site.id,
      name: site.name,
      code: site.slug ?? site.id,
      slug: site.slug ?? undefined,
      description: site.description ?? undefined,
    };
  }

  private resetState(): void {
    this.sitesSubject.next([]);
    this.switchableSitesSubject.next([]);
    this.activeSiteIdSubject.next(null);
    localStorage.removeItem(this.ACTIVE_SITE_KEY);
  }
}
